import React, { useMemo, useState } from 'react'
import {
  AlertTriangle as AlertTriangleIcon,
  Clock as ClockIcon,
  Info as InfoIcon,
  Layers as LayersIcon,
} from 'react-feather'
import {
  Box,
  Button,
  Container,
  Grid,
  Hidden,
  Paper,
  TablePagination,
  Typography,
} from '@material-ui/core'

import {
  BaseStatusCard,
  ContentHeader,
  FilterButton,
  Page,
  Permitted,
} from 'components'
import {
  TreatmentCreationModal,
  TreatmentFilters,
  TreatmentTable,
} from './components'

import helpers from 'helpers'

import useFilter from 'hooks/useFilter'
import useFetch from 'hooks/useFetch'
import usePagination from 'hooks/usePagination'

import constants from 'constants/index'
import * as service from 'service'
import useStyles from './styles'
import theme from 'theme'

const DataProcessTreatmentMain = () => {
  const [openCreateModal, setOpenCreateModal] = useState(false)

  const filter = useFilter()
  const classes = useStyles()

  const { perPage, page, handleChangePage, handleChangeRowsPerPage } =
    usePagination(10)

  const { handleStatusCard } = helpers.baseStatusCard

  const { STATUS, STATUS_LABELS } = constants.dataProcessTreatment

  const statsFilters = useMemo(() => {
    const {
      page: _page,
      perPage: _perPage,
      status: _status,
      ...rest
    } = filter.filters

    return rest
  }, [JSON.stringify(filter.filters)])

  const { response, isLoading, refresh } = useFetch(
    service.dponet.dataProcessTreatment.get,
    {
      ...filter.filters,
      page,
      perPage,
    },
    [page, perPage, filter.filters],
  )

  const dataProcessTreatments = response?.data?.dataProcessTreatments

  const { response: statsResponse, isLoading: statsIsLoading } = useFetch(
    service.dponet.dataProcessTreatment.stats,
    {
      ...statsFilters,
    },
    [statsFilters],
  )

  const stats = helpers.changeCase.keys.camelCase(statsResponse?.data?.stats)

  const { response: riskResponse, isLoading: isLoadingRisk } = useFetch(
    service.dponet.matrixRisk.list,
    {
      minimal: true,
    },
    [],
  )

  const matrixRisks = riskResponse?.data?.matrixRisks || []

  const { response: departmentsResponse, isLoading: isLoadingDepartments } =
    useFetch(
      service.dponet.departments.get,
      {
        perPage: 9999,
        order: 'name ASC',
        minimal: true,
      },
      [],
    )

  const departments = departmentsResponse?.data?.departments || []

  const STATUS_CARD_ITEMS = [
    {
      status: STATUS.PENDING_ID,
      title: `${stats?.pending || 0} Tratamentos`,
      subTitle: STATUS_LABELS.PENDING_LABEL + 's',
      Icon: ClockIcon,
      iconBgColor: theme.palette.yellowWarningLight,
    },
    {
      status: STATUS.IN_PROGRESS_ID,
      title: `${stats?.inProgress || 0} Tratamentos`,
      subTitle: STATUS_LABELS.IN_PROGRESS_LABEL + 's',
      Icon: InfoIcon,
      iconBgColor: theme.palette.info.main,
    },
    {
      status: STATUS.OVERDUE_ID,
      title: `${stats?.overdue || 0} Tratamentos`,
      subTitle: STATUS_LABELS.OVERDUE_LABEL + 's',
      Icon: AlertTriangleIcon,
      iconBgColor: theme.palette.error.main,
    },
    {
      status: STATUS.TREATED_ID,
      title: `${stats?.treated || 0} Tratamentos`,
      subTitle: STATUS_LABELS.TREATED_LABEL + 's',
      Icon: LayersIcon,
      iconBgColor: theme.palette.success.light,
    },
  ]

  const handleOpenCreateModal = () => setOpenCreateModal(true)

  return (
    <Page title="Tratamento de Processos">
      <Container maxWidth={false} className={classes.container}>
        <ContentHeader title="Tratamento de Processos">
          <Permitted
            someTags={[
              constants.permissions.DATA_PROCESS_TREATMENT.MANAGE,
              constants.permissions.DATA_PROCESSES.CREATE,
            ]}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenCreateModal}
              fullWidth
              disabled={isLoadingDepartments}
            >
              Novo Tratamento
            </Button>
          </Permitted>
          <FilterButton
            setDrawerOpen={filter.setDrawerOpen}
            fullWidth
            isLoading={isLoadingRisk || isLoadingDepartments}
          />
        </ContentHeader>
        <Box display="flex" flexDirection="column" gridGap={theme.spacing(4)}>
          <Typography variant="h4" color="primary">
            Filtre por etapa
          </Typography>
          <Grid container spacing={2}>
            {STATUS_CARD_ITEMS.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <BaseStatusCard
                  {...item}
                  selected={filter.filters?.status === item?.status}
                  onClick={() =>
                    handleStatusCard({ filter, value: item?.status })
                  }
                  isLoading={statsIsLoading}
                />
              </Grid>
            ))}
          </Grid>
          <Box>
            <Paper>
              <TreatmentTable
                dataProcessTreatments={dataProcessTreatments}
                isLoading={isLoading}
                refresh={refresh}
              />
              <TablePagination
                component="div"
                count={response?.data?.meta?.totalCount || 0}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                page={page - 1}
                rowsPerPage={perPage}
                rowsPerPageOptions={[5, 10, 25, 100]}
                labelRowsPerPage={<Hidden smDown>Por página </Hidden>}
                nextIconButtonProps={{ size: 'small' }}
                backIconButtonProps={{ size: 'small' }}
              />
            </Paper>
          </Box>
        </Box>
        {filter.drawerOpen && (
          <TreatmentFilters
            filter={filter}
            matrixRisks={matrixRisks}
            departments={departments}
          />
        )}
        {openCreateModal && (
          <TreatmentCreationModal
            open={openCreateModal}
            setOpen={setOpenCreateModal}
            departments={departments}
            refresh={refresh}
          />
        )}
      </Container>
    </Page>
  )
}

export default DataProcessTreatmentMain
